import { cn } from '@/lib/utils';

/**
 * Pulsing red dot used as the "live timer" indicator across the app — header
 * pill, popover running-timer card, and the inline timesheet cell indicator.
 *
 * Renders a single `bg-red-500 rounded-full` span with `animate-pulse` at
 * a 1.6s ease-in-out duration (matches the BigTime-style "heartbeat" spec;
 * Tailwind's default `animate-pulse` is 2s). Calm, professional — not the
 * radar `animate-ping` variant which reads as "urgent" and is too noisy on
 * the dense timesheet grid.
 *
 * Tailwind v4 note: `duration-[1600ms]` targets `transition-duration`,
 * NOT `animation-duration`, so it would be a visual no-op on `animate-*`
 * utilities. Use the arbitrary-CSS-property form `[animation-duration:1600ms]`
 * to override the keyframe duration directly.
 *
 * Use `pointer-events-none` on callers stacking over a clickable parent
 * (e.g. the cell indicator) so the dot doesn't steal clicks.
 */
const SIZE_CLASSES = {
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
} as const;

export function PulsingDot({
  size = 'sm',
  className,
}: {
  size?: keyof typeof SIZE_CLASSES;
  className?: string;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block rounded-full bg-red-500 animate-pulse [animation-duration:1600ms]',
        SIZE_CLASSES[size],
        className,
      )}
    />
  );
}
